import { pgSelect, pgInsert, pgUpdateWhere, uploadPublicFile, isSupabaseConfigured } from './supabaseClient';
import { logEvent } from './debugLog';

/**
 * Architectural floor-plan images as 2D-map underlays (2026-07-12, user
 * request: "will attached zip floor plan help for 2D?"). One image per
 * detected floor band, uploaded from the Dashboard's 2D view and stored in
 * venue_settings.floor_plans with a manual calibration (meters-per-pixel,
 * world offset, rotation) that aligns the drawing with the scanned navmesh.
 */
export interface FloorPlanCal {
  /** Floor band index (Map2D order, lowest elevation first). */
  band: number;
  /** Public image URL (poi-media bucket, floorplans/<map>/…). */
  url: string;
  /** Meters per image pixel. */
  mpp: number;
  /** World X/Z of the image's top-left pixel. */
  x: number;
  z: number;
  /** Rotation (deg, clockwise) about the top-left pixel. */
  rotDeg: number;
  /** Plan opacity 0..1 (default 1 — in v2 the plan IS the map). */
  opacity?: number;
}

/** An explicitly configured floor: display label + base elevation (map m). */
export interface FloorDef {
  label: string;
  y: number;
}

const CACHE_KEY = 'navme.cache.floorPlans';
const FLOORS_CACHE_KEY = 'navme.cache.floors';

/** Explicit floor list (Dashboard-configured). Empty = auto-detect. */
export async function getFloors(mapCode: string): Promise<FloorDef[]> {
  if (!isSupabaseConfigured() || !mapCode) return [];
  try {
    const rows = await pgSelect<{ floors: FloorDef[] | null }>(
      'venue_settings',
      `select=floors&map_code=eq.${encodeURIComponent(mapCode)}`,
    );
    const floors = rows[0]?.floors ?? [];
    try {
      localStorage.setItem(FLOORS_CACHE_KEY, JSON.stringify(floors));
    } catch {
      /* quota */
    }
    return floors;
  } catch {
    try {
      return (JSON.parse(localStorage.getItem(FLOORS_CACHE_KEY) ?? '[]') as FloorDef[]) ?? [];
    } catch {
      return [];
    }
  }
}

/** Staff-only (RLS-enforced): set the venue's explicit floor list. */
export async function saveFloors(mapCode: string, floors: FloorDef[]): Promise<void> {
  const updated = await pgUpdateWhere(
    'venue_settings',
    `map_code=eq.${encodeURIComponent(mapCode)}`,
    { floors },
  );
  if (updated === 0) {
    await pgInsert('venue_settings', { map_code: mapCode, floors });
  }
  logEvent('admin', `floors saved (${floors.length}): ${floors.map((f) => `${f.label}@${f.y}m`).join(', ')}`);
}

export async function getFloorPlans(mapCode: string): Promise<FloorPlanCal[]> {
  if (!isSupabaseConfigured() || !mapCode) return [];
  try {
    const rows = await pgSelect<{ floor_plans: FloorPlanCal[] | null }>(
      'venue_settings',
      `select=floor_plans&map_code=eq.${encodeURIComponent(mapCode)}`,
    );
    const plans = rows[0]?.floor_plans ?? [];
    try {
      localStorage.setItem(CACHE_KEY, JSON.stringify(plans));
    } catch {
      /* quota */
    }
    return plans;
  } catch {
    try {
      return (JSON.parse(localStorage.getItem(CACHE_KEY) ?? '[]') as FloorPlanCal[]) ?? [];
    } catch {
      return [];
    }
  }
}

/** Staff-only (RLS-enforced): replace the venue's floor-plan set. */
export async function saveFloorPlans(mapCode: string, plans: FloorPlanCal[]): Promise<void> {
  const updated = await pgUpdateWhere(
    'venue_settings',
    `map_code=eq.${encodeURIComponent(mapCode)}`,
    { floor_plans: plans },
  );
  if (updated === 0) {
    await pgInsert('venue_settings', { map_code: mapCode, floor_plans: plans });
  }
  logEvent('admin', `floor plans saved (${plans.length})`);
}

/** Upload a plan image → public URL (poi-media bucket). */
export async function uploadFloorPlanImage(mapCode: string, band: number, file: File): Promise<string> {
  const ext = (file.name.split('.').pop() || 'png').toLowerCase();
  return uploadPublicFile('poi-media', `floorplans/${mapCode}/floor-${band}-${Date.now()}.${ext}`, file);
}

// ---------- Floor links (cross-floor connectors) ----------

/**
 * An authored connector between two floors — the stairs/elevator/escalator
 * the scan couldn't make walkable (stairwells are common VPS/scan dead
 * zones, so the generated navmesh often leaves floors as disconnected
 * islands — real venue 2026-07-12: "navmesh path is partial … floors
 * likely disconnected"). Marked in the Dashboard's 2D view with one tap on
 * each floor; routeService stitches navmesh legs through them.
 */
export interface FloorLink {
  id: string;
  label: string;
  kind: 'stairs' | 'elevator' | 'escalator';
  /** Endpoint on one floor (map-space; y = that floor's walkable height). */
  a: { x: number; y: number; z: number };
  /** Endpoint on the other floor. */
  b: { x: number; y: number; z: number };
  /** Wheelchair-accessible connector (elevators yes, stairs no). */
  accessible?: boolean;
}

const LINKS_CACHE_KEY = 'navme.cache.floorLinks';

export async function getFloorLinks(mapCode: string): Promise<FloorLink[]> {
  if (!isSupabaseConfigured() || !mapCode) return [];
  try {
    const rows = await pgSelect<{ floor_links: FloorLink[] | null }>(
      'venue_settings',
      `select=floor_links&map_code=eq.${encodeURIComponent(mapCode)}`,
    );
    const links = rows[0]?.floor_links ?? [];
    try {
      localStorage.setItem(LINKS_CACHE_KEY, JSON.stringify(links));
    } catch {
      /* quota */
    }
    return links;
  } catch {
    try {
      return (JSON.parse(localStorage.getItem(LINKS_CACHE_KEY) ?? '[]') as FloorLink[]) ?? [];
    } catch {
      return [];
    }
  }
}

/** Staff-only (RLS-enforced): replace the venue's floor-link set. */
export async function saveFloorLinks(mapCode: string, links: FloorLink[]): Promise<void> {
  const updated = await pgUpdateWhere(
    'venue_settings',
    `map_code=eq.${encodeURIComponent(mapCode)}`,
    { floor_links: links },
  );
  if (updated === 0) {
    await pgInsert('venue_settings', { map_code: mapCode, floor_links: links });
  }
  logEvent('admin', `floor links saved (${links.length}): ${links.map((l) => `${l.kind} "${l.label}"`).join(', ')}`);
}
